import produce from "immer";
import { Suspense, useRef } from "react";
import { Controller, FormProvider } from "react-hook-form";

import {
  AvailableIntegrationFragment,
  ConnectionBaseFragment,
  ObjectTypeFragment,
  OperationModeOptionFragment,
  useListObjectTypesSuspenseQuery,
  useObjectTypeSettingsLazyQuery,
} from "@/apollo/types";
import AppearTransition from "@/components/elements/AppearTransition";
import { Button } from "@/components/elements/Button";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import { Heading } from "@/components/elements/Typography";
import { TaskAction } from "@/components/modules/TaskActionBar";
import { useStepsContext } from "@/components/modules/stepper";
import FieldLabel from "@/components/primitives/InputLabel";
import { formatModelName } from "@/features/models";
import { useDisclosure } from "@/hooks/useDisclosure";
import { useMountEffect } from "@/hooks/useMountEffect";
import { useIntegration } from "@/integrations/hooks/useIntegration";
import { useDataWarehouseContext } from "@/providers/DataWarehouseProvider";
import {
  NextButton,
  PrevButton,
} from "@/widgets/syncs/shared/ui/StepNavButton";
import { Cog6ToothIcon } from "@heroicons/react/16/solid";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { ArrowDownIcon } from "@heroicons/react/24/solid";

import {
  PrimaryPropertyMapping,
  PropertyMapping,
  useReverseETLStateContext,
} from "../../state/StepStateContext";
import { MappingDataProvider } from "./MappingDataContext";
import { FormValues, SourceModel, useMappingForm } from "./form";
import { DestinationObjectSelector } from "./ui/components/DestinationObjectSelector";
import { FieldErrorMessage } from "./ui/components/FieldErrorMessage";
import { FieldMappingSkeleton } from "./ui/components/FieldMappingSkeleton";
import { ModelSelector } from "./ui/components/ModelSelector";
import { ObjectTypeSettingsDialog } from "./ui/components/ObjectTypeSettingsDialog";
import { OperationModeExplainer } from "./ui/components/OperationModeExplainer";
import { FieldMappingSection } from "./ui/sections/FieldMappingSection";
import { PrimaryMappingSection } from "./ui/sections/PrimaryMappingSection";
import { SyncModeSection } from "./ui/sections/SyncModeSection";
import { useMappingData } from "./useMappingData";

export function ModelMappingStep() {
  const { state } = useReverseETLStateContext();
  const connection = state.destination.value?.connection;
  const integration = useIntegration(connection?.integrationId);
  if (!connection || !integration) {
    return null;
  }
  return (
    <Suspense
      fallback={
        <div className="flex justify-center py-10">
          <LoadingSpinner />
        </div>
      }
    >
      <ModelMappingStepInner
        connection={connection}
        integration={integration}
      />
    </Suspense>
  );
}

export function ModelMappingStepInner(props: {
  connection: ConnectionBaseFragment;
  integration: AvailableIntegrationFragment;
}) {
  const stepsContext = useStepsContext();
  const { state, setState } = useReverseETLStateContext();
  const dwh = useDataWarehouseContext();

  const { data } = useListObjectTypesSuspenseQuery({
    variables: {
      connectionId: props.connection.id,
    },
  });
  const objectTypes = data.objectTypes;

  const formMethods = useMappingForm(state.modelMapping.value, objectTypes);

  const mappingData = useMappingData(props.connection.id, formMethods);

  const objectTypeButtonRef = useRef<HTMLButtonElement | null>(null);

  const selectedModel = formMethods.watch("model");
  const selectedObjectType = formMethods.watch("objectTypeOption");
  const objectTypeSettings = formMethods.watch("objectTypeSettings");
  const selectedOperation = formMethods.watch("operation");

  const objectTypeSettingsDisclosure = useDisclosure();

  const handleSubmit = (values: FormValues) => {
    let primaryMapping: PrimaryPropertyMapping | null = null;
    if (selectedOperation.primaryKeyRequired) {
      if (
        values.primaryMapping?.sourceProperty == null ||
        values.primaryMapping?.destinationProperty == null
      ) {
        formMethods.setError("primaryMapping.sourceProperty", {
          type: "required",
          message: "Primary source property is required",
        });
        formMethods.setError("primaryMapping.destinationProperty", {
          type: "required",
          message: "Primary destination property is required",
        });
        return;
      }
      primaryMapping = {
        sourceProperty: values.primaryMapping.sourceProperty,
        destinationProperty: values.primaryMapping.destinationProperty,
      };
    }

    setState(
      produce((state) => {
        state.modelMapping = {
          value: {
            model: values.model!,
            mappings: values.mappings
              .map((x): PropertyMapping | null => {
                if (x.sourceProperty == null || x.destinationProperty == null) {
                  return null;
                }
                const mapping: PropertyMapping = {
                  destinationProperty: x.destinationProperty,
                  sourceProperty: x.sourceProperty,
                };
                return mapping;
              })
              .filter((x): x is PropertyMapping => x != null),
            primaryMapping: primaryMapping,
            objectType: values.objectTypeOption!,
            objectTypeSettings: values.objectTypeSettings ?? undefined,
            operation: values.operation,
          },
          isValid: true,
          isDirty: false,
        };
      }),
    );
    stepsContext.onNextStep();
  };

  const handleSubmitErrors = (errors: any) => {
    //console.log("errors", errors);
  };

  const [fetchObjectTypeSettings, { data: objectTypeSettingsData }] =
    useObjectTypeSettingsLazyQuery();

  useMountEffect(async () => {
    // If we already have an object type selected on mount, fetch its settings
    if (selectedObjectType) {
      fetchObjectTypeSettings({
        variables: {
          connectionId: props.connection.id,
          objectType: selectedObjectType.objectName,
        },
      });
    }
  });

  async function initObjectType(
    objectType: ObjectTypeFragment,
    modelId?: string,
  ) {
    let newOperationMode: OperationModeOptionFragment | undefined = undefined;
    // Fetch supported operations for the new object type
    const operationsResult = await mappingData.loadOperations(
      objectType.objectName,
    );

    if (operationsResult && operationsResult.operations.length > 0) {
      // Match the selected operation mode, or default to the first one
      const selectedOperation = operationsResult.operations.find(
        (op) =>
          op.operationMode === formMethods.getValues("operation.operationMode"),
      );
      newOperationMode = selectedOperation ?? operationsResult.operations[0];
      formMethods.setValue("operation", newOperationMode);

      // Fetch the destination properties for the new operation mode
      const destinationProperties = await mappingData.loadDestinationProperties(
        {
          objectType: objectType.objectName,
          operationMode: newOperationMode.operationMode,
          modelId,
        },
      );
      return {
        operationMode: newOperationMode,
        destinationProperties: destinationProperties?.properties,
      };
    }
    return {
      operationMode: newOperationMode,
      destinationProperties: null,
    };
  }

  async function handleModelChange(
    next: SourceModel | null,
    prev: SourceModel | null,
  ) {
    if (!next) return;
    if (prev?.id === next.id) return;

    formMethods.setValue("mappings", []);
    formMethods.clearErrors("model");
    formMethods.clearErrors("mappings");

    // Fetch source and destination properties
    const promises = [
      mappingData.loadSourceProperties(next.id),
      selectedObjectType
        ? initObjectType(selectedObjectType, next.id)
        : Promise.resolve(null),
    ] as const;

    const [sourceProperties, objectTypeResult] = await Promise.all(promises);

    if (sourceProperties && objectTypeResult?.destinationProperties) {
      // Initialize mappings
      mappingData.initializeMappings(
        sourceProperties.properties,
        objectTypeResult.destinationProperties,
      );
    }
  }

  // invoked when user picks a new Destination Object
  async function handleObjectTypeChange(
    next: ObjectTypeFragment | null,
    prev: ObjectTypeFragment | null,
  ) {
    if (!next) return;
    if (prev?.objectName === next.objectName) return;
    // clear any old values and settings if the object type has changed
    formMethods.setValue("objectTypeSettings", null);
    formMethods.setValue("mappings", []);
    formMethods.setValue("primaryMapping", null);

    formMethods.clearErrors("objectTypeOption");
    formMethods.clearErrors("objectTypeSettings");
    formMethods.clearErrors("mappings");
    formMethods.clearErrors("primaryMapping");

    // Initialize operation mode and destination properties for the new object type
    const { operationMode, destinationProperties } = await initObjectType(
      next,
      selectedModel?.id,
    );

    if (destinationProperties) {
      if (
        operationMode.primaryKeyRequired ||
        destinationProperties.some((x) => x.isPrimaryKey)
      ) {
        // Reset primary mapping if the new operation requires or supports a primary key
        formMethods.setValue("primaryMapping", {
          sourceProperty: undefined,
          destinationProperty: undefined,
        });
      }

      if (mappingData.sourceProperties.all) {
        // Initialize mappings with the new destination properties
        mappingData.initializeMappings(
          mappingData.sourceProperties.all,
          destinationProperties,
        );
      }
    }

    // fetch new settings for the new object type
    const { data } = await fetchObjectTypeSettings({
      variables: {
        connectionId: props.connection.id,
        objectType: next.objectName,
      },
    });
    const settings = data?.getObjectTypeSettings.settings;
    if (settings) {
      // open the settings dialog if there are settings
      objectTypeSettingsDisclosure.onOpen();
    }
  }

  async function handleOperationModeChange(
    next: OperationModeOptionFragment | null,
    prev: OperationModeOptionFragment | null,
  ) {
    if (!next) return;
    if (prev?.operationMode !== next.operationMode) {
      mappingData.loadDestinationProperties({
        operationMode: next.operationMode,
      });
    }
  }

  const objectTypeSettingsForm =
    objectTypeSettingsData?.getObjectTypeSettings.settings;

  return (
    <MappingDataProvider {...mappingData}>
      <div className="mb-6">
        <Heading className="mb-1">Map Your Data</Heading>
        <p className="text-sm text-muted-foreground">
          Define how your source data should sync with your destination. Map
          columns and set up unique identifiers to ensure accurate data
          integration.
        </p>
      </div>
      <FormProvider {...formMethods}>
        <form
          id="model-mapping-form"
          onSubmit={formMethods.handleSubmit(handleSubmit, handleSubmitErrors)}
          className="w-full"
        >
          <div className="flex flex-col items-center gap-x-4 gap-y-2 sm:flex-row sm:items-start">
            <div className="w-full flex-1">
              <Controller
                name="model"
                control={formMethods.control}
                render={({ field }) => (
                  <div>
                    <FieldLabel>Source Model</FieldLabel>
                    <ModelSelector
                      {...field}
                      nullable
                      onChange={(value) => {
                        if (selectedObjectType == null) {
                          objectTypeButtonRef.current?.click();
                        }
                        handleModelChange(value, field.value);
                        field.onChange(value);
                      }}
                      defaultOpen={selectedModel == null}
                    />
                  </div>
                )}
                rules={{
                  validate: (value) =>
                    !!value || "Please select a source model",
                }}
              />
              <FieldErrorMessage<FormValues>
                name={`model`}
                onRetry={() => {
                  handleModelChange(selectedModel, null);
                }}
              />
            </div>
            <div className="flex flex-none items-center justify-center">
              <ArrowDownIcon className="h-5 w-5 text-muted-foreground sm:mt-10 sm:-rotate-90" />
            </div>
            <div className="w-full flex-1">
              <Controller<FormValues, "objectTypeOption">
                name="objectTypeOption"
                render={({ field }) => (
                  <div>
                    <FieldLabel>Destination Object</FieldLabel>
                    <DestinationObjectSelector
                      integrationId={props.integration.id}
                      integrationName={props.integration.name}
                      options={objectTypes}
                      connectionLabel={props.connection.label}
                      buttonRef={objectTypeButtonRef}
                      {...field}
                      onChange={(value) => {
                        handleObjectTypeChange(value, field.value);
                        field.onChange(value);
                      }}
                    />

                    {objectTypeSettingsForm && (
                      <div className="mx-1 mt-0.5 flex items-center justify-end gap-1">
                        <input
                          type="hidden"
                          {...formMethods.register("objectTypeSettings", {
                            validate: (value) => {
                              if (objectTypeSettingsForm.required && !value) {
                                return "Settings are required for this destination object";
                              }
                              return true;
                            },
                          })}
                        />
                        {objectTypeSettings && (
                          <CheckCircleIcon className="h-4 w-4 text-green-600" />
                        )}
                        <Button
                          variant="link"
                          size="sm"
                          iconRight={<Cog6ToothIcon />}
                          onClick={() => objectTypeSettingsDisclosure.onOpen()}
                          className="text-muted-foreground"
                        >
                          Edit Settings
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                rules={{
                  validate: (value) =>
                    !!value || "Please select a destination object",
                }}
              />
              <FieldErrorMessage<FormValues>
                name={`objectTypeOption`}
                onRetry={() => {
                  handleObjectTypeChange(selectedObjectType, null);
                }}
              />
              <FieldErrorMessage<FormValues> name={`objectTypeSettings`} />
            </div>
          </div>
          <div className="pb-20">
            {selectedModel && selectedObjectType && (
              <div className="mt-10 space-y-10">
                {mappingData.isLoading ? (
                  <FieldMappingSkeleton rows={5} />
                ) : (
                  <>
                    <div>
                      <div>
                        <SyncModeSection
                          operations={mappingData.operations}
                          onChange={handleOperationModeChange}
                        />
                      </div>

                      {(selectedOperation?.primaryKeyRequired ||
                        mappingData.destinationProperties
                          .hasPrimaryKeyProperties) && (
                        <AppearTransition>
                          <div className="mt-10">
                            <PrimaryMappingSection
                              destinationIntegrationId={
                                props.connection.integrationId
                              }
                              modelName={formatModelName(selectedModel)}
                              isRequired={selectedOperation?.primaryKeyRequired}
                            />
                          </div>
                        </AppearTransition>
                      )}

                      <OperationModeExplainer
                        sourceIntegration={dwh.integration}
                        destinationIntegration={props.integration}
                        className="mt-8"
                      />
                    </div>

                    <div>
                      <FieldMappingSection
                        destinationIntegrationId={
                          props.connection.integrationId
                        }
                        modelName={formatModelName(selectedModel)}
                        objectType={selectedObjectType}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <TaskAction>
            <div className="flex flex-row-reverse gap-4">
              <NextButton type="submit" form="model-mapping-form" />
              <PrevButton />
            </div>
          </TaskAction>
        </form>
      </FormProvider>

      <ObjectTypeSettingsDialog
        {...objectTypeSettingsDisclosure}
        integrationId={props.integration.id}
        connectionId={props.connection.id}
        objectType={formMethods.watch("objectTypeOption.objectName")}
        objectTypeSettings={objectTypeSettings}
        onSave={(settings) => {
          formMethods.setValue("objectTypeSettings", settings, {
            shouldValidate: true,
          });
          objectTypeSettingsDisclosure.onClose();
        }}
        onClose={() => {
          objectTypeSettingsDisclosure.onClose();
          const settings = formMethods.getValues("objectTypeSettings");
          if (!settings) {
            formMethods.setError("objectTypeSettings", {
              message: "Settings are required for this destination object",
              type: "required",
            });
          }
        }}
      />
    </MappingDataProvider>
  );
}
